import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, useUser } from '@clerk/clerk-react';

const API_BASE = process.env.REACT_APP_API_URL;

const STATUS_COLORS = {
  Draft: '#94a3b8',
  'In Review': '#60a5fa',
  Released: '#46BB89',
  Superseded: '#f59e0b',
  Obsolete: '#EC2124',
  Archived: '#64748b',
};

const ALL_STATUSES = ['Draft', 'In Review', 'Released', 'Superseded', 'Obsolete'];
const ALL_LEVELS = ['MASTER', 'FAMILY', 'SPECIFIC'];
const LEVEL_LABELS = { MASTER: 'Master', FAMILY: 'Family', SPECIFIC: 'Specific' };
const KNOWN_SUPPLIERS = ['gpv', 'ubiqconn', 'acmepoint', 'hk_ringer'];

const DOC_TYPES = [
  'Master Quality Procedure',
  'Product Family Quality Procedure',
  'Product-Specific Quality Procedure',
  'Inspection Procedure',
  'Visual Acceptance Criteria',
  'Test Procedure',
  'Work Instruction',
  'Evidence Requirement',
];

export default function QualityDocumentLibrary() {
  const { getToken } = useAuth();
  const { user } = useUser();
  const navigate = useNavigate();

  const _roleRaw = user?.publicMetadata?.role || (Array.isArray(user?.publicMetadata?.roles) ? user.publicMetadata.roles[0] : user?.publicMetadata?.roles) || '';
  const role = String(_roleRaw).toLowerCase().trim();
  const canManage = ['admin', 'quality-manager', 'production-engineer', 'service'].includes(role);
  const isSupplier = !['admin', 'quality-manager', 'production-engineer', 'service'].includes(role) && !!role;
  const supplierKey = ['gpv','ubiqconn','acmepoint','hk_ringer'].includes(role.replace(/^test-/, ''));
  const canContribute = canManage || supplierKey;

  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAllFilters, setShowAllFilters] = useState(false);
  const [filters, setFilters] = useState({
    status: 'Released',
    level: '',
    documentType: '',
    search: '',
    productNumber: '',
    supplier: '',
    owner: '',
  });

  const fetchDocs = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (filters.status) params.set('status', filters.status);
      if (filters.level) params.set('level', filters.level);
      if (filters.documentType) params.set('documentType', filters.documentType);
      if (filters.search) params.set('search', filters.search);
      if (filters.productNumber) params.set('productNumber', filters.productNumber);
      if (filters.supplier) params.set('supplier', filters.supplier);
      if (filters.owner) params.set('owner', filters.owner);

      const res = await fetch(`${API_BASE}/quality/documents?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (data.ok) setDocs(data.documents || []);
    } catch {
      // backend not yet reachable
    } finally {
      setLoading(false);
    }
  }, [getToken, filters]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  const setFilter = (key, val) => setFilters(f => ({ ...f, [key]: val }));

  const clearFilters = () => setFilters({
    status: 'Released',
    level: '', documentType: '', search: '',
    productNumber: '', supplier: '', owner: '',
  });

  const activeFilterCount = Object.entries(filters)
    .filter(([k, v]) => {
      if (!v) return false;
      if (k === 'status' && isSupplier) return false;
      if (k === 'status' && v === 'Released') return false;
      return true;
    })
    .length;

  const filterStyle = {
    background: 'var(--ni-panel)',
    border: '1px solid var(--ni-border-soft)',
    color: 'var(--ni-text)',
    borderRadius: 6,
    padding: '6px 10px',
    fontSize: 13,
  };

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 20, fontWeight: 800 }}>Document Library</h2>
        {canContribute && (
          <button
            onClick={() => navigate('/quality/documents/new')}
            style={{ background: 'var(--ni-green)', color: '#000', border: 'none', borderRadius: 6, padding: '8px 18px', fontWeight: 700, fontSize: 13, cursor: 'pointer' }}
          >
            + New Document
          </button>
        )}
      </div>

      {/* Filters row 1 — always visible */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="text"
          placeholder="Search number or title..."
          value={filters.search}
          onChange={e => setFilter('search', e.target.value)}
          style={{ ...filterStyle, minWidth: 220 }}
        />
        {!isSupplier && (
          <select value={filters.status} onChange={e => setFilter('status', e.target.value)} style={filterStyle}>
            <option value="">All statuses</option>
            {ALL_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
        )}
        <select value={filters.level} onChange={e => setFilter('level', e.target.value)} style={filterStyle}>
          <option value="">All levels</option>
          {ALL_LEVELS.map(l => <option key={l} value={l}>{LEVEL_LABELS[l]}</option>)}
        </select>
        <select value={filters.documentType} onChange={e => setFilter('documentType', e.target.value)} style={filterStyle}>
          <option value="">All types</option>
          {DOC_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
        </select>
        <button
          onClick={() => setShowAllFilters(f => !f)}
          style={{ ...filterStyle, cursor: 'pointer', color: showAllFilters ? 'var(--ni-green)' : 'var(--ni-text-muted)' }}
        >
          More filters{activeFilterCount > 0 ? ` (${activeFilterCount})` : ''}
        </button>
        {activeFilterCount > 0 && (
          <button onClick={clearFilters} style={{ ...filterStyle, cursor: 'pointer', color: 'var(--ni-text-muted)' }}>
            Clear all
          </button>
        )}
      </div>

      {/* Filters row 2 — expanded */}
      {showAllFilters && (
        <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap', alignItems: 'center', padding: '12px 16px', background: 'var(--ni-panel)', borderRadius: 8, border: '1px solid var(--ni-border-soft)' }}>
          <div>
            <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Product No.</div>
            <input
              type="text"
              placeholder="e.g. WE270"
              value={filters.productNumber}
              onChange={e => setFilter('productNumber', e.target.value)}
              style={{ ...filterStyle, width: 140 }}
            />
          </div>
          {!isSupplier && (
            <div>
              <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Supplier</div>
              <select value={filters.supplier} onChange={e => setFilter('supplier', e.target.value)} style={filterStyle}>
                <option value="">All suppliers</option>
                {KNOWN_SUPPLIERS.map(s => <option key={s} value={s}>{s.toUpperCase()}</option>)}
              </select>
            </div>
          )}
          <div>
            <div style={{ fontSize: 11, color: 'var(--ni-text-muted)', marginBottom: 4, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Owner</div>
            <input
              type="text"
              placeholder="Name or team"
              value={filters.owner}
              onChange={e => setFilter('owner', e.target.value)}
              style={{ ...filterStyle, width: 160 }}
            />
          </div>
        </div>
      )}

      {/* Table */}
      <div style={{ background: 'var(--ni-panel)', border: '1px solid var(--ni-border-soft)', borderRadius: 10, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, color: 'var(--ni-text-muted)', textAlign: 'center' }}>Loading...</div>
        ) : docs.length === 0 ? (
          <div style={{ padding: 40, color: 'var(--ni-text-muted)', textAlign: 'center' }}>
            No documents found.
            {canContribute && <> <button onClick={() => navigate('/quality/documents/new')} style={{ background: 'none', border: 'none', color: 'var(--ni-green)', cursor: 'pointer', fontSize: 14 }}>Create one.</button></>}
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ color: 'var(--ni-text-muted)', fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.06em', background: 'rgba(0,0,0,0.2)' }}>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Doc No.</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Title</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Level</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Type</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Family</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Supplier visibility</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Rev.</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Status</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Effective</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600 }}>Owner</th>
                  <th style={{ padding: '10px 16px', textAlign: 'left', fontWeight: 600, whiteSpace: 'nowrap' }}>Updated</th>
                </tr>
              </thead>
              <tbody>
                {docs.map(doc => (
                  <tr
                    key={doc._id}
                    onClick={() => navigate(`/quality/documents/${doc._id}`)}
                    style={{ cursor: 'pointer', borderTop: '1px solid var(--ni-border-soft)' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(255,255,255,0.03)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <td style={{ padding: '11px 16px', fontFamily: 'monospace', fontSize: 12, color: 'var(--ni-green)', whiteSpace: 'nowrap' }}>{doc.documentNumber}</td>
                    <td style={{ padding: '11px 16px', fontWeight: 600, maxWidth: 260 }}>{doc.title}</td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                      <LevelBadge level={doc.level} />
                    </td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', fontSize: 12, maxWidth: 160 }}>{doc.documentType}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)' }}>{doc.productFamily || '—'}</td>
                    <td style={{ padding: '11px 16px' }}>
                      <SupplierVisibility doc={doc} />
                    </td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', fontFamily: 'monospace', whiteSpace: 'nowrap' }}>{doc.currentRevision || '—'}</td>
                    <td style={{ padding: '11px 16px', whiteSpace: 'nowrap' }}>
                      <StatusBadge status={doc.currentStatus} />
                    </td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap' }}>
                      {doc.effectiveDate ? new Date(doc.effectiveDate).toLocaleDateString() : '—'}
                    </td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)' }}>{doc.owner || '—'}</td>
                    <td style={{ padding: '11px 16px', color: 'var(--ni-text-muted)', whiteSpace: 'nowrap' }}>
                      {doc.updatedAt ? new Date(doc.updatedAt).toLocaleDateString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, color: 'var(--ni-text-muted)', fontSize: 12 }}>{docs.length} document{docs.length !== 1 ? 's' : ''}</div>
    </div>
  );
}

function SupplierVisibility({ doc }) {
  const suppliers = doc.visibility?.suppliers || [];
  const access = doc.visibility?.supplierAccess || 'None';
  if (access === 'None' || suppliers.length === 0) {
    return <span style={{ color: 'var(--ni-text-muted)', fontSize: 12 }}>Internal only</span>;
  }
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {suppliers.map(s => (
        <span key={s} style={{ fontSize: 10, fontWeight: 700, padding: '1px 6px', borderRadius: 3, background: 'rgba(96,165,250,0.15)', color: '#60a5fa', textTransform: 'uppercase' }}>
          {s}
        </span>
      ))}
    </div>
  );
}

function StatusBadge({ status }) {
  const color = STATUS_COLORS[status] || '#666';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 10px', borderRadius: 20,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: color + '22', color,
    }}>
      {status}
    </span>
  );
}

function LevelBadge({ level }) {
  const colors = { MASTER: '#a78bfa', FAMILY: '#60a5fa', SPECIFIC: '#34d399' };
  const color = colors[level] || '#aaa';
  return (
    <span style={{
      display: 'inline-block', padding: '2px 8px', borderRadius: 4,
      fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase',
      background: color + '22', color,
    }}>
      {LEVEL_LABELS[level] || level}
    </span>
  );
}
